export interface SubscriptionFormViewModel {
  permitNumber: string;
  memberId: string;
  societies: SocietyViewModel[];
  period: SubscriptionPeriod;
  premiumMinimum: PremiumTypeViewModel;
  surplus: PremiumTypeViewModel[];
  defaultSurplusToSelect: PremiumTypeViewModel;
  subprime: number;
  selectedStartDate: Date;
  credit: number;
  showPremiumMinimum: boolean;
  premiumBaseAmount: number;
  allowSurplusRefusal: boolean;
  isPaymentByCheckedAllowed: boolean;
  isExtension: boolean;
  surplusTakenWhenMemberBecameInactive: number;
  memberInactiveDate: number;
}

export interface SocietyViewModel {
  id: string;
  neq: string;
  legalName: string;
}

export interface SubscriptionPeriod {
  id: string;
  description: string;
  year: number;
  startDate: Date;
  endDate: Date;
  effectiveStartDate: Date;
  effectiveEndDate: Date;
  formOpeningDate: Date;
  minSurplusAmountPerSociety: number;
  isCurrentYear: boolean;
  subscriptionNoticeId: string;
  isAnnual: boolean;
  editable: boolean;
  hasParticularConditions: boolean;
  receipts: SubscriptionReceipt[];
  isSubscriptionBlocked: boolean;
  isWithNonReportedReclamation: boolean;
  isExtension: boolean;
}

export interface SubscriptionReceipt {
  id: string;
  description: string;
  year: number;
  receiptDate: Date;
  receiptTotal: number;
}

export interface PremiumTypeViewModel {
  premiumId: string;
  premiumCode: string;
  premiumAmount: number;
  calcCredit: number;
  calcPremiumBase: number;
  calcSubprime: number;
  calcTPA: number;
  calcTotal: number;
  selected: boolean;
  premiumAmountWithoutBase: number;
  periodLimitAmount: number;
}

export class SelectedPremiumInfo {
  permitNumber: string;
  memberId: string;
  subscriptionPeriodId: string;
  subscriptionNoticeId: string;
  year: number;
  surplusId: string;
  userRefuseSurplus: boolean;
  //selected values (in this order in the table)
  premiumAmount: number;
  premiumAmountWithoutBase: number;
  credit: number;
  premiumBase: number;
  subprime: number;
  tpa: number;
  total: number;
  premiumSelectedId: string;
  //payment info
  paymentAmount: string;
  paymentDataKey: string;
  paymentLanguage: string;
  paymentNameUnderCard: string;
  //for test: although it is only for dev and localhost it needs to be removed
  bypassMoneris: boolean;
  requestDate: string;
  isPaymentByCheck: boolean;

  public calculateTotals(model: SubscriptionFormViewModel) {
    var premiumSelected = model.premiumMinimum;
    if (this.userRefuseSurplus == false) {
      premiumSelected = model.surplus.find(
        (s) => s.premiumId == this.surplusId
      );
    }
    this.credit = premiumSelected.calcCredit;
    this.premiumSelectedId = premiumSelected.premiumId;
    this.premiumAmount = premiumSelected.premiumAmount;
    this.premiumBase = premiumSelected.calcPremiumBase;
    this.subprime = premiumSelected.calcSubprime;
    this.tpa = premiumSelected.calcTPA;
    this.total = premiumSelected.calcTotal;
    this.premiumAmountWithoutBase = premiumSelected.premiumAmountWithoutBase;
  }
  public setPaymentInfo(
    paymentDataKey: string,
    nameUnderCard: string,
    bypassMoneris: boolean,
    isPaymentByCheck: boolean
  ) {
    this.paymentDataKey = paymentDataKey;
    this.paymentNameUnderCard = nameUnderCard;
    this.paymentLanguage = "CA";
    this.paymentAmount = this.total.toString();
    this.bypassMoneris = bypassMoneris;
    this.isPaymentByCheck = isPaymentByCheck;
  }
  public setCreditInfo() {
    this.resetPaymentInfo();
    this.paymentAmount = this.total.toString();
  }
  public resetPaymentInfo() {
    this.paymentAmount = null;
    this.paymentDataKey = null;
    this.paymentLanguage = null;
    this.paymentNameUnderCard = null;
    this.bypassMoneris = false;
    this.isPaymentByCheck = false;
  }
}

export interface PaymentResultModelView {
  confirmationModel: PaymentReceipt;
  requestDate: Date;
  startDateLog: Date;
  endDateLog: Date;
  paymentSteps: PaymentStepDetail[];
  isStatusResponsibilityInsuranceExist: boolean;
  memberInsuranceStatus: MemberInsuranceStatus;
}
export interface PaymentStepDetail {
  paymentStep: string;
  dateTimeStart: Date;
  dateTimeEnd: Date;
  description: string;
}

export interface PaymentReceipt {
  amount: string;
  authorizationNumber: string;
  creditCardNumber: string;
  date: string;
  message: string;
  monerisCardType: string;
  monerisTransactionTypeRef: string;
  nameUnderCard: string;
  orderId: string;
  referenceNumber: string;
  respondentPermitNumber: string;
  responseCode: string;
  transactionResult: string;
}
export interface PaymentErrorModel {
  requiresRestartForm: boolean;
  message: string;
}

export interface MemberInsuranceViewModel {
  id: string;
  permitNumber: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: string;
  preferredLanguage: string;
  email: string;
  isExemptFromInsurance: boolean;
  isWaitingForInsurance: boolean;
  effectiveExerciseStatus: string;
  effectiveInscriptionStatus: string;
  societiesCount?: number;
  financialYear: number;
  premiumsInYearCount?: number;
  isActiveExercise: boolean;
  isActiveInscription: boolean;
  hasOpenSubscriptions: boolean;
  isAnnual: boolean;
  startDate: string;
  endDate: string;
  surchargePercentage?: number;
  premiumBaseCode: string;
  premiumBaseCoverage?: number;
  premiumBaseFundAmount?: number;
  premiumBaseReinsurerAmount?: number;
  premiumBaseTvqAmount?: number;
  premiumBaseTpsAmount?: number;
  premiumBaseSurchageAmount?: number;
  premiumBaseTotalAmount?: number;
  minSurplusPremiumCode: string;
  minSurplusPremiumCoverage?: number;
  minSurplusFundAmount?: number;
  minSurplusReinsurerAmount?: number;
  minSurplusTvqAmount?: number;
  minSurplusTpsAmount?: number;
  minSurplusSurchageAmount?: number;
  minSurplusTotalAmount?: number;
  totalMinPremiumCoverage?: number;
  totalFundAmount?: number;
  totalReinsurerAmount?: number;
  totalTvqAmount?: number;
  totalTpsAmount?: number;
  totalSurchageAmount?: number;
  totalAmount?: number;
}
export class MemberInsuranceStatus {
  id: string;
  insuranceStatusCodeId: string;
  effectiveOn: Date;
}

export class SubscriptionNoticePreselection {
  public surplusPremiumId: string;
  public subscriptionNoticeId: string;
  public financialYear: number;
  public isReclamationReported: boolean;
}

export class SubscriptionProlongationViewModel {
  public isInactiveMemberForSubscription: boolean;
}
